(() => {
  if (window.__faDeliveryLiveUIBootstrapped) return;
  window.__faDeliveryLiveUIBootstrapped = true;

  const localISODate = (date) => {
    const value = date || new Date();
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatHour = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    if (raw.includes(':')) return raw;
    const hour = Number.parseInt(raw, 10);
    if (!Number.isFinite(hour)) return raw;
    return `${String(hour).padStart(2, '0')}:00`;
  };

  function initWidget(widget) {
    if (!widget || widget.dataset.faLiveUiInitialized === 'true') return;

    const datePicker = widget.querySelector('[data-fa-date-picker]');
    const dateStrip = widget.querySelector('[data-fa-date-strip]');
    const openCalendar = widget.querySelector('[data-fa-open-calendar]');
    const timeslotSelect = widget.querySelector('[data-fa-timeslot-select]');
    const timeslotList = widget.querySelector('[data-fa-timeslot-list]');
    const timeslotMessage = widget.querySelector('[data-fa-timeslot-message]');
    const changeDateButton = widget.querySelector('[data-fa-change-date]');

    if (!datePicker || !dateStrip || !timeslotSelect || !timeslotList) return;
    widget.dataset.faLiveUiInitialized = 'true';

    function syncDateButtons() {
      const selected = String(datePicker.value || '');
      dateStrip.querySelectorAll('.fa-date-pill').forEach((button) => {
        const active = Boolean(selected && button.dataset.date === selected);
        button.classList.toggle('fa-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function buildDateStrip() {
      dateStrip.innerHTML = '';
      const today = new Date();
      today.setHours(12, 0, 0, 0);

      for (let index = 0; index < 7; index += 1) {
        const date = new Date(today);
        date.setDate(today.getDate() + index);
        const iso = localISODate(date);
        const dayLabel = index === 0
          ? 'Today'
          : date.toLocaleDateString('en-MY', { weekday: 'short' });

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fa-date-pill';
        button.dataset.date = iso;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute(
          'aria-label',
          `${dayLabel}, ${date.toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}`
        );

        const day = document.createElement('span');
        day.className = 'fa-date-pill-day';
        day.textContent = dayLabel;

        const number = document.createElement('span');
        number.className = 'fa-date-pill-date';
        number.textContent = String(date.getDate());

        button.append(day, number);
        button.addEventListener('click', () => {
          if (datePicker.value === iso) {
            syncDateButtons();
            return;
          }
          datePicker.value = iso;
          syncDateButtons();
          datePicker.dispatchEvent(new Event('change', { bubbles: true }));
        });

        dateStrip.appendChild(button);
      }

      syncDateButtons();
    }

    function syncTimeslotCards() {
      const selected = String(timeslotSelect.value || '');
      timeslotList.querySelectorAll('.fa-timeslot-card').forEach((card) => {
        const active = Boolean(selected && card.dataset.value === selected);
        card.classList.toggle('fa-active', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function renderTimeslots() {
      timeslotList.innerHTML = '';
      const placeholder = String(timeslotSelect.options?.[0]?.textContent || '').trim();
      const options = Array.from(timeslotSelect.options || []).filter((option) => option.value);

      if (!options.length) {
        if (timeslotMessage) {
          timeslotMessage.textContent = /loading/i.test(placeholder) ? placeholder : '';
        }
        return;
      }

      if (timeslotMessage) timeslotMessage.textContent = '';

      options.forEach((option) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'fa-timeslot-card';
        card.dataset.value = String(option.value);
        card.setAttribute('aria-pressed', 'false');
        card.disabled = Boolean(timeslotSelect.disabled || option.disabled);
        if (card.disabled) card.classList.add('fa-disabled');

        const title = document.createElement('span');
        title.className = 'fa-timeslot-title';
        title.textContent = String(option.dataset.text || option.textContent || 'Timeslot').trim();

        const range = document.createElement('span');
        range.className = 'fa-timeslot-range';
        const start = formatHour(option.dataset.start);
        const end = formatHour(option.dataset.end);
        const alreadyHasRange = /\d{1,2}(?::\d{2})?\s*(?:am|pm)?\s*[-–]\s*\d{1,2}/i.test(title.textContent);
        range.textContent = !alreadyHasRange && start && end ? `${start} – ${end}` : '';

        card.append(title, range);
        card.addEventListener('click', () => {
          if (card.disabled) return;
          timeslotSelect.value = card.dataset.value;
          syncTimeslotCards();
          timeslotSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });

        timeslotList.appendChild(card);
      });

      syncTimeslotCards();
    }

    buildDateStrip();
    renderTimeslots();

    datePicker.addEventListener('change', syncDateButtons);
    timeslotSelect.addEventListener('change', syncTimeslotCards);

    if (openCalendar) {
      openCalendar.addEventListener('click', () => {
        try {
          datePicker.focus({ preventScroll: true });
          if (typeof datePicker.showPicker === 'function') datePicker.showPicker();
          else datePicker.click();
        } catch (_) {
          datePicker.click();
        }
      });
    }

    if (changeDateButton) {
      changeDateButton.addEventListener('click', () => {
        window.setTimeout(syncDateButtons, 0);
      });
    }

    new MutationObserver(renderTimeslots).observe(timeslotSelect, {
      childList: true,
      attributes: true,
      attributeFilter: ['disabled']
    });
  }

  function initAll(root = document) {
    root.querySelectorAll?.('[data-fa-delivery-widget]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-fa-delivery-widget]')) initWidget(node);
        else initAll(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
